import { useContext, useEffect, useState } from "react";

import { Typography } from "@/components/Typography";
import { Form } from "@/components/Form";
import { QREditorContextProps, QRProps } from "@/contexts/QREditorContext/types";
import { QREditorContext } from "@/contexts/QREditorContext";
import { applyQRFormDefaultValues, onBindClick, onUpdateFormFields } from "@/utils/formHelper";
import { omittedValues, onBindSocialNetworks, onBindThemePicker } from "@/utils/qrEditorHelper";
import { videoValidation } from "@/utils/validators/qr-videos";
import { QRVideoProps } from "./types";
import { FileProps } from "@/components/FilesUpload/types";
import { FormValidationMethods } from "@/components/Form/types";
import { NAMESPACES } from "@/internals/constants/namespaces";
import { useTranslation } from "react-i18next";
import "./styles.scss";

export function QRVideo({ data }: QRVideoProps) {
  const { t } = useTranslation(NAMESPACES.VALIDATIONS);
  const { qrData, setQrData } = useContext<QREditorContextProps<"video">>(QREditorContext);

  const [qrVideoData, setQrVideoData] = useState<QRProps<"video">>(qrData);
  const [renderForm, setRenderForm] = useState(false);
  const [isAddVideoButtonDisabled, setIsAddVideoButtonDisabled] = useState(false);
  const [url, setUrl] = useState("");

  const [dataVideoWithDefaultValues, setDataVideoWithDefaultValues] = useState(
    applyQRFormDefaultValues(data, qrData.data)
  );

  onBindThemePicker(dataVideoWithDefaultValues, "videoThemePicker", omittedValues(qrData.data));
  onBindSocialNetworks(
    dataVideoWithDefaultValues,
    "videoSocials",
    "socialNetworks",
    omittedValues(qrData.data)
  );
  onBindClick(dataVideoWithDefaultValues, "videoContentUploadButton", () => {
    if (!url) return;

    setQrData({
      ...qrData,
      data: {
        ...qrData.data,
        videoUrls: qrData?.data?.videoUrls
          ? [...qrData.data.videoUrls, { name: url, size: undefined, url, type: "video" }]
          : [{ name: url, size: undefined, url, type: "video" }],
      },
    });
    setUrl(undefined);

    const videoUrlInput = document.getElementById("videoContentUrl") as HTMLInputElement;
    if (videoUrlInput) {
      videoUrlInput.value = "";
    }
  });
  onBindClick(dataVideoWithDefaultValues, "videoUrls", (files: FileProps[]) => {
    setQrData({
      ...qrData,
      data: {
        ...qrData.data,
        videoUrls: files,
      },
    });
  });

  const onUpdateState = ({ field, value, methods }) => {
    if (field === "file" || field == "") return;
    if (field === "videoContentUrl") {
      checkUrl(value, methods);
      return;
    }

    setQrData(
      {
        ...qrData,
        data: {
          ...qrData.data,
          [field]: value,
        },
      },
      omittedValues(qrData.data)
    );
  };

  const checkUrl = (url: string, methods: FormValidationMethods) => {
    setUrl(url);
    const videoUrlValidation = methods.trigger("videoContentUrl");
    videoUrlValidation.then((isValid) => {
      setIsAddVideoButtonDisabled(!isValid);
    });
  };

  useEffect(() => {
    setQrVideoData(qrData);
  }, [qrData]);

  useEffect(() => {
    setQrData({ ...qrVideoData, isValidForm: renderForm });
  }, [renderForm]);

  useEffect(() => {
    const videoUrlsSize = qrData?.data?.videoUrls?.length;
    const newData = onUpdateFormFields(
      dataVideoWithDefaultValues,
      "videoContentUploadButton",
      "disabled",
      isAddVideoButtonDisabled || !url || videoUrlsSize >= 10 ? true : false
    );

    setDataVideoWithDefaultValues(newData);
  }, [isAddVideoButtonDisabled, url, qrData?.data?.videoUrls]);

  return (
    <div className="qr-video">
      <Typography
        Tag="p"
        size="xl"
        classname="u-bold u-spacing-5"
      >
        {data.title}
      </Typography>
      <Form
        fields={dataVideoWithDefaultValues}
        schema={videoValidation(data, t)}
        onWatchFields={({ field, value, methods }) => onUpdateState({ field, value, methods })}
        isFormValid={(isValidForm) => setRenderForm(isValidForm)}
      />
    </div>
  );
}
